import React, { useState, useEffect } from "react";
import { Search, Printer, ArrowRight, CheckCircle } from "lucide-react";
import { api } from "../../utils/api";

export default function CreditorsReport({ onDetailClick }) {
  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [threshold, setThreshold] = useState(0); 

  useEffect(() => {
    const fetchRealBalances = async () => {
      try {
        const ownersRes = await api.get("/owners");
        const ownersList = ownersRes.data || [];

        const detailedOwners = await Promise.all(
          ownersList.map(async (o) => {
            const code = o.accounting_code || o.hesap_kodu || o.HesapKodu || o.account_code;
            if (!code) return { ...o, real_bakiye: 0, safe_code: "Bilinmiyor" };

            try {
              const statementRes = await api.get(`/statement/${code}`);
              const currentBalance = statementRes.data?.totals?.balance || 0;
              return { ...o, real_bakiye: currentBalance, safe_code: code };
            } catch (err) {
              return { ...o, real_bakiye: 0, safe_code: code };
            }
          })
        );

        setOwners(detailedOwners);
        setLoading(false);
      } catch (err) {
        console.error("Alacaklılar hesaplanırken hata oluştu:", err);
        setLoading(false);
      }
    };

    fetchRealBalances();
  }, []);

  // SADECE GÜNCEL EKSTRE BAKİYESİ KESİNLİKLE 0'DAN KÜÇÜK (ALACAKLI) OLANLARI FİLTRELE
  // threshold (süzgeç) pozitif girilse de alacak mantığına uyarlamak için eksiyle çarpıyoruz
  const creditors = owners.filter(o => o.real_bakiye < -1 && Math.abs(o.real_bakiye) >= threshold)
                        .sort((a, b) => a.real_bakiye - b.real_bakiye);
                        
  const totalCredit = creditors.reduce((sum, o) => sum + Math.abs(o.real_bakiye), 0);

  const formatTL = (value) => new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY" }).format(value);

  const handlePrint = () => {
    window.print();
  };

  const printTitle = threshold > 0 
    ? `BAŞKENT SİTESİ/APARTMANI ${formatTL(threshold)} ve ÜZERİ ALACAKLI OLANLAR LİSTESİ` 
    : `BAŞKENT SİTESİ/APARTMANI ALACAKLILAR LİSTESİ`;

  if (loading) return (
    <div style={{ padding: "80px", textAlign: "center", color: "#64748b", display: "flex", flexDirection: "column", gap: "16px", alignItems: "center", animation: "fadeIn 0.3s ease-in" }}>
      <div style={{ width: "40px", height: "40px", border: "4px solid #e2e8f0", borderTopColor: "#10b981", borderRadius: "50%", animation: "spin 1s linear infinite" }}></div>
      <style>{`@keyframes spin { 100% { transform: rotate(360deg); } }`}</style>
      <div>
        <h3 style={{ margin: "0 0 6px 0", color: "#1e293b" }}>Hesap Dökümleri Taranıyor...</h3>
        <p style={{ margin: 0, fontSize: "14px" }}>Fazla ödeme yapan villalar hesaplanıyor, lütfen bekleyin.</p>
      </div>
    </div>
  );

  return (
    <div className="creditors-report-container" style={{ animation: "fadeIn 0.4s ease-out" }}>
      
      <style>{`
        @media print {
          body * { visibility: hidden; }
          .creditors-print-area, .creditors-print-area * { visibility: visible; }
          .creditors-print-area { position: absolute; left: 0; top: 0; width: 100%; }
          .creditors-print-area .no-print { display: none !important; }
          .print-header { display: block !important; text-align: center; margin-bottom: 25px; border-bottom: 2px solid #000; padding-bottom: 15px; }
          .print-header h2 { font-size: 20px; margin: 0 0 8px 0; color: #000; }
          .print-header p { font-size: 14px; margin: 0; color: #333; }
          table { width: 100%; border-collapse: collapse; }
          th, td { border: 1px solid #000 !important; padding: 10px !important; color: #000 !important; }
          th { background-color: #f1f5f9 !important; -webkit-print-color-adjust: exact; }
        }
        .print-header { display: none; }
      `}</style>

      <div className="no-print" style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", background: "white", padding: "20px 24px", borderRadius: "16px", border: "1px solid #e2e8f0", boxShadow: "0 4px 6px rgba(0,0,0,0.02)", marginBottom: "24px", gap: "20px" }}>
        
        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <div style={{ width: "48px", height: "48px", borderRadius: "12px", background: "#ecfdf5", display: "flex", alignItems: "center", justifyContent: "center", color: "#10b981" }}>
            <CheckCircle size={24} />
          </div>
          <div>
            <h2 style={{ margin: "0 0 4px 0", fontSize: "18px", color: "#1e293b" }}>Alacaklılar Listesi (Fazla Ödeyenler)</h2>
            <p style={{ margin: 0, fontSize: "13px", color: "#64748b" }}>Toplam {creditors.length} kayıt listeleniyor</p>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px", background: "#f8fafc", padding: "6px 12px", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
            <Search size={16} color="#64748b" />
            <span style={{ fontSize: "13px", color: "#475569", fontWeight: "500" }}>Süzgeç (TL):</span>
            <input 
              type="number" 
              value={threshold} 
              onChange={e => setThreshold(Number(e.target.value))}
              min="0"
              style={{ width: "80px", background: "transparent", border: "none", outline: "none", fontWeight: "bold", color: "#1e293b" }}
            />
            <span style={{ fontSize: "13px", color: "#475569" }}>ve üzeri</span>
          </div>
          <button onClick={handlePrint} style={{ display: "flex", alignItems: "center", gap: "8px", background: "#1e293b", color: "white", border: "none", padding: "10px 16px", borderRadius: "8px", fontWeight: "600", cursor: "pointer", transition: "background 0.2s" }}>
            <Printer size={18} /> PDF / Yazdır
          </button>
        </div>
      </div>

      <div className="creditors-print-area">
        
        <div className="print-header">
          <h2>{printTitle}</h2>
          <p>Rapor Tarihi: {new Date().toLocaleDateString("tr-TR")} - Saat: {new Date().toLocaleTimeString("tr-TR", { hour: '2-digit', minute:'2-digit' })}</p>
        </div>

        <div style={{ background: "white", borderRadius: "16px", border: "1px solid #e2e8f0", boxShadow: "0 4px 6px rgba(0,0,0,0.02)", overflow: "hidden" }} className="table-container">
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left" }}>
              <thead>
                <tr style={{ background: "#f8fafc", borderBottom: "2px solid #e2e8f0" }}>
                  <th style={{ padding: "16px", fontSize: "13px", color: "#64748b", fontWeight: "600" }}>Daire Adı</th>
                  <th style={{ padding: "16px", fontSize: "13px", color: "#64748b", fontWeight: "600" }}>Hesap Kodu</th>
                  <th style={{ padding: "16px", fontSize: "13px", color: "#64748b", fontWeight: "600" }}>Malik Adı Soyadı</th>
                  <th style={{ padding: "16px", fontSize: "13px", color: "#64748b", fontWeight: "600" }}>Oturan Adı Soyadı</th>
                  <th style={{ padding: "16px", fontSize: "13px", color: "#64748b", fontWeight: "600", textAlign: "right" }}>Alacak Tutarı</th>
                  <th className="no-print" style={{ padding: "16px", fontSize: "13px", color: "#64748b", fontWeight: "600", textAlign: "center" }}>Ayrıntı</th>
                </tr>
              </thead>
              <tbody>
                {creditors.length === 0 ? (
                  <tr>
                    <td colSpan="6" style={{ padding: "40px", textAlign: "center", color: "#94a3b8" }}>Belirlenen limite uygun alacaklı bulunamadı.</td>
                  </tr>
                ) : creditors.map((o, i) => (
                  <tr key={i} style={{ borderBottom: "1px solid #f1f5f9", transition: "background 0.2s" }} onMouseOver={e => e.currentTarget.style.background = '#f8fafc'} onMouseOut={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={{ padding: "14px 16px", fontWeight: "700", color: "#334155" }}>
                      {o.villa_no || o.daire_kodu || "—"}
                    </td>
                    <td style={{ padding: "14px 16px", color: "#64748b", fontSize: "14px" }}>
                      {o.safe_code}
                    </td>
                    <td style={{ padding: "14px 16px", fontWeight: "500", color: "#1e293b" }}>
                      {o.owner_name || o.malik_adi || "—"}
                    </td>
                    <td style={{ padding: "14px 16px", color: "#475569" }}>
                      {o.tenant_name || o.oturan_adi || "—"}
                    </td>
                    <td style={{ padding: "14px 16px", textAlign: "right", fontWeight: "bold", color: "#10b981", fontSize: "15px" }}>
                      {/* Ekrana basarken pozitif gösterilir ki kafa karışıklığı olmasın */}
                      {formatTL(Math.abs(o.real_bakiye))}
                    </td>
                    <td className="no-print" style={{ padding: "14px 16px", textAlign: "center" }}>
                      <button 
                        onClick={() => onDetailClick(o.safe_code)}
                        style={{ display: "inline-flex", alignItems: "center", gap: "6px", background: "#eff6ff", color: "#3b82f6", border: "1px solid #bfdbfe", padding: "6px 12px", borderRadius: "6px", cursor: "pointer", fontSize: "13px", fontWeight: "600" }}
                      >
                        Ekstre <ArrowRight size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ background: "#ecfdf5", borderTop: "2px solid #a7f3d0" }}>
                  <td colSpan="4" style={{ padding: "16px", textAlign: "right", fontWeight: "bold", color: "#065f46" }}>GENEL TOPLAM:</td>
                  <td style={{ padding: "16px", textAlign: "right", fontWeight: "900", color: "#047857", fontSize: "18px" }}>{formatTL(totalCredit)}</td>
                  <td className="no-print"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

      </div>
    </div>
  );
}